import { Component } from '@theme/component';

/**
 * @typedef {Object} TabsComponentRefs
 * @property {HTMLButtonElement[]} tabButtons - Tab button elements
 * @property {HTMLElement[]} tabPanels - Tab panel elements
 */

/**
 * Accessible tabs component following WAI-ARIA Tab Pattern.
 *
 * @extends {Component<TabsComponentRefs>}
 */
class TabsComponent extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Generate tab buttons from panels
    this.#generateTabButtons();

    // Set up IDs and ARIA relationships
    this.#setupAccessibility();

    // Set initial state
    this.#activateTab(0);

    // Add keyboard navigation
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  /**
   * Generates tab buttons from panel data attributes.
   */
  #generateTabButtons() {
    const tabList = this.refs.tabList;
    const panels = this.refs.tabPanels;

    if (!tabList || !panels?.length) return;

    // Clear any existing buttons first
    tabList.innerHTML = '';

    panels.forEach((panel, index) => {
      const label = panel.dataset.tabLabel || 'Tab';
      const button = document.createElement('button');
      
      button.setAttribute('ref', 'tabButtons[]');
      button.className = 'product-specs__tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('tabindex', '-1');
      button.textContent = label;
      
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.#activateTab(index);
      });

      tabList.appendChild(button);
    });

    // Store reference to generated buttons
    this.refs.tabButtons = Array.from(tabList.querySelectorAll('[ref="tabButtons[]"]'));
  }

  /**
   * Sets up IDs and ARIA attributes for tabs and panels.
   */
  #setupAccessibility() {
    const buttons = this.refs.tabButtons;
    const panels = this.refs.tabPanels;
    const componentId = this.id || `tabs-${Math.random().toString(36).substr(2, 9)}`;

    if (!buttons?.length || !panels?.length) return;

    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      const panel = panels[i];
      const tabId = `tab-${i + 1}-${componentId}`;
      const panelId = `panel-${i + 1}-${componentId}`;

      if (button) {
        button.id = tabId;
        button.setAttribute('aria-controls', panelId);
      }

      if (panel) {
        panel.id = panelId;
        panel.setAttribute('aria-labelledby', tabId);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles tab button clicks.
   *
   * @param {number} index - The tab index to activate.
   * @param {Event} event - The click event.
   */
  handleTabClick(index, event) {
    event.preventDefault();
    this.#activateTab(index);
  }

  /**
   * Activates a tab by index.
   *
   * @param {number} index - The tab index to activate.
   */
  #activateTab(index) {
    const buttons = this.refs.tabButtons;
    const panels = this.refs.tabPanels;

    if (!buttons?.length || !panels?.length) return;

    // Update all tabs and panels
    for (let i = 0; i < buttons.length; i++) {
      const isActive = i === index;
      const button = buttons[i];
      const panel = panels[i];

      if (button) {
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.setAttribute('tabindex', isActive ? '0' : '-1');
      }

      if (panel) {
        panel.hidden = !isActive;
      }
    }
  }

  /**
   * Handles keyboard navigation for tabs.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    const buttons = this.refs.tabButtons;

    if (!buttons?.length) return;

    // Only handle keyboard events on tab buttons
    const target = event.target;

    if (!(target instanceof HTMLElement) || !target.hasAttribute('role') || target.getAttribute('role') !== 'tab') {
      return;
    }

    const currentIndex = buttons.indexOf(/** @type {HTMLButtonElement} */ (target));

    if (currentIndex === -1) return;

    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex === buttons.length - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = buttons.length - 1;
        break;
      default:
        return;
    }

    this.#activateTab(newIndex);
    buttons[newIndex]?.focus();
  };
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
